import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import {
  createMacdConnectPix,
  getMacdConnectPagamentoStatus,
} from "@/lib/api";
import { useReveal } from "@/hooks/useReveal";
import { Loader2, Copy, Check, AlertTriangle } from "lucide-react";

/**
 * MACD Connect Presencial — Pagamento (PIX)
 *
 * Two visual states are handled here:
 *   • pix_ready  → QR Code + Pix Copia-e-Cola + polling for confirmation
 *   • pix_error  → lead already saved on the backend, PIX generation failed;
 *                   user can retry without re-submitting the form.
 *
 * The confirmation ("PAGAMENTO CONFIRMADO") lives on /macd-connect/confirmado
 * and is only reached after the polling detects payment_status === "paid".
 */
export default function MacdConnectPagamento() {
  const rootRef = useReveal();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  // Guard: this page only makes sense after the form has been submitted and
  // a lead_id was created. Send visitors that arrive here directly back to
  // the landing.
  useEffect(() => {
    if (!state.lead_id) {
      navigate("/macd-connect", { replace: true });
      return;
    }
    // Scroll-to-top on mount so the QR Code is above the fold after
    // navigating from the form.
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [state.lead_id, navigate]);

  const [pix, setPix] = useState(state.pix || null);
  const [pixError, setPixError] = useState(state.pixError || null);
  const [copied, setCopied] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const pollingRef = useRef(null);

  const retry = useCallback(async () => {
    if (!state.lead_id) return;
    setRetrying(true);
    setPixError(null);
    try {
      const fresh = await createMacdConnectPix({ lead_id: state.lead_id });
      setPix(fresh);
    } catch (err) {
      setPixError(err?.detail || err?.message || "erro_desconhecido");
    } finally {
      setRetrying(false);
    }
  }, [state.lead_id]);

  // Polling — only while we have an active PIX.
  useEffect(() => {
    if (!state.lead_id || !pix) return undefined;
    let cancelled = false;
    let attempts = 0;
    const MAX = 90; // ~6 minutes at 4s

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const s = await getMacdConnectPagamentoStatus(state.lead_id);
        if (cancelled) return;
        if (s.payment_status === "paid") {
          navigate("/macd-connect/confirmado", {
            state: {
              nome: state.nome,
              lead_id: state.lead_id,
              paid: true,
              paid_at: s.payment_paid_at,
            },
            replace: true,
          });
          return;
        }
        if (["expired", "cancelled"].includes(s.payment_status)) {
          setPix(null);
          setPixError(`pix_${s.payment_status}`);
          return;
        }
      } catch (_) {
        // Transient failures don't stop the polling — the user can always
        // retry manually if it stays stuck.
      }
      if (attempts < MAX && !cancelled) {
        pollingRef.current = setTimeout(tick, 4500);
      }
    };
    pollingRef.current = setTimeout(tick, 4500);
    return () => {
      cancelled = true;
      if (pollingRef.current) clearTimeout(pollingRef.current);
    };
  }, [pix, state.lead_id, state.nome, navigate]);

  const doCopy = async () => {
    if (!pix?.qr_code) return;
    try {
      await navigator.clipboard.writeText(pix.qr_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_) {
      // Fallback silencioso — botão ainda troca visualmente para "copiado"
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const fmtValidity = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (_) {
      return null;
    }
  };

  return (
    <div
      ref={rootRef}
      className="min-h-screen bg-[#f4f2ec] text-[#0a0a0a] antialiased selection:bg-[#f4c542]/40"
      data-testid="mc-pagamento-page"
    >
      <Navbar />

      <main className="pt-24 md:pt-32 pb-24 md:pb-32">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left column — narrative */}
          <div className="lg:col-span-5 reveal">
            <span className="eyebrow text-[#8a8a8a]">Vaga · Pagamento</span>
            <h1
              data-testid="mc-pagamento-title"
              className="mt-6 font-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.02] tracking-tight text-[#0a0a0a]"
            >
              Sua inscrição
              <br />
              <em className="italic">foi recebida.</em>
            </h1>
            <p className="mt-8 max-w-md text-lg leading-relaxed text-[#2a2a2a]">
              Falta apenas concluir o PIX de{" "}
              <span className="font-medium text-[#0a0a0a]">R$ 97</span> para
              confirmar sua vaga no{" "}
              <span className="font-medium text-[#0a0a0a]">
                MACD Connect Presencial
              </span>
              .
            </p>

            <div className="mt-10 pt-8 border-t border-[#0a0a0a]/15 space-y-2">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#8a8a8a]">
                Ingresso individual
              </div>
              <div className="font-display text-5xl md:text-6xl tracking-tight text-[#0a0a0a]">
                R$ 97
              </div>
              <div className="text-[13px] text-[#4a4a4a] mt-2">
                31 de agosto de 2026 · 10h às 18h30 · São Paulo
              </div>
            </div>

            <div className="mt-10 text-[13px] leading-[1.7] text-[#4a4a4a] max-w-md">
              A confirmação da vaga é feita automaticamente pelo Mercado Pago
              assim que o PIX é reconhecido. Você não precisa nos enviar o
              comprovante.
            </div>
          </div>

          {/* Right column — PIX or error */}
          <div className="lg:col-span-7 reveal">
            {pix ? (
              <div
                className="bg-white border border-[#0a0a0a]/8 p-8 md:p-10"
                data-testid="mc-pix-ready"
              >
                <div className="text-[11px] tracking-[0.24em] uppercase text-[#8a8a8a]">
                  Pagamento via PIX
                </div>
                <h2 className="mt-2 font-display text-2xl md:text-[1.75rem] leading-tight tracking-tight text-[#0a0a0a]">
                  Escaneie o QR Code
                  <br />
                  <em className="italic text-[#0a0a0a]/60">ou copie o código.</em>
                </h2>

                <div className="mt-8 flex flex-col md:flex-row items-start gap-8">
                  <div className="mx-auto md:mx-0 shrink-0">
                    {pix.qr_code_base64 ? (
                      <img
                        src={`data:image/png;base64,${pix.qr_code_base64}`}
                        alt="QR Code PIX MACD Connect Presencial"
                        data-testid="mc-pix-qrcode"
                        className="w-[220px] h-[220px] md:w-[240px] md:h-[240px] border border-[#0a0a0a]/10"
                      />
                    ) : (
                      <div className="w-[220px] h-[220px] md:w-[240px] md:h-[240px] border border-[#0a0a0a]/10 flex items-center justify-center text-[#0a0a0a]/40 text-xs">
                        QR indisponível
                      </div>
                    )}
                  </div>

                  <div className="flex-1 w-full min-w-0">
                    <div className="text-[11px] tracking-[0.24em] uppercase text-[#8a8a8a] mb-2">
                      PIX copia e cola
                    </div>
                    <div
                      data-testid="mc-pix-copiaecola"
                      className="text-[12px] md:text-[13px] leading-relaxed font-mono break-all bg-[#f4f2ec] border border-[#0a0a0a]/10 p-3 max-h-[140px] overflow-y-auto text-[#2a2a2a]"
                    >
                      {pix.qr_code || "—"}
                    </div>
                    <button
                      onClick={doCopy}
                      type="button"
                      data-testid="mc-pix-copy-btn"
                      className="mt-4 inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-[13px] uppercase tracking-[0.14em] px-5 py-3 hover:bg-[#f4c542] hover:text-[#0a0a0a] transition-colors"
                    >
                      {copied ? (
                        <>
                          <Check size={14} /> Código copiado
                        </>
                      ) : (
                        <>
                          <Copy size={14} /> Copiar código PIX
                        </>
                      )}
                    </button>
                    {fmtValidity(pix.date_of_expiration) && (
                      <p className="mt-4 text-[11px] tracking-[0.16em] uppercase text-[#8a8a8a]">
                        Válido até {fmtValidity(pix.date_of_expiration)}
                      </p>
                    )}
                  </div>
                </div>

                <div
                  data-testid="mc-pix-polling"
                  className="mt-10 pt-8 border-t border-[#0a0a0a]/10 flex items-center gap-3 text-[13px] text-[#4a4a4a]"
                >
                  <Loader2 className="animate-spin text-[#8a8a8a]" size={16} />
                  Aguardando confirmação do pagamento…
                </div>
              </div>
            ) : (
              <div
                className="bg-white border border-[#0a0a0a]/8 p-8 md:p-10"
                data-testid="mc-pix-error"
              >
                <div className="flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[#8a4a00]">
                  <AlertTriangle size={14} /> Cobrança indisponível no momento
                </div>
                <h2 className="mt-4 font-display text-2xl md:text-[1.75rem] leading-tight tracking-tight text-[#0a0a0a]">
                  Sua inscrição foi recebida,
                  <br />
                  <em className="italic text-[#0a0a0a]/60">
                    mas não conseguimos gerar o PIX agora.
                  </em>
                </h2>
                <p className="mt-6 text-[15px] leading-relaxed text-[#2a2a2a] max-w-md">
                  Tente novamente em alguns instantes. Se persistir, nossa
                  equipe entrará em contato pelo WhatsApp para concluir a
                  reserva da sua vaga.
                </p>

                <button
                  onClick={retry}
                  type="button"
                  disabled={retrying}
                  data-testid="mc-pix-retry-btn"
                  className="mt-8 inline-flex items-center gap-2 bg-[#0a0a0a] text-white text-[13px] uppercase tracking-[0.14em] px-6 py-3 hover:bg-[#f4c542] hover:text-[#0a0a0a] transition-colors disabled:opacity-60"
                >
                  {retrying ? (
                    <>
                      <Loader2 className="animate-spin" size={14} /> Tentando…
                    </>
                  ) : (
                    "Tentar gerar PIX novamente"
                  )}
                </button>

                <p className="mt-6 text-[11px] tracking-[0.16em] uppercase text-[#8a8a8a]">
                  Código técnico: {pixError || "unknown"}
                </p>
              </div>
            )}

            <div className="mt-6 text-[12px] text-[#6b6b6b]">
              <Link
                to="/macd-connect"
                className="underline underline-offset-4 hover:text-[#0a0a0a]"
              >
                ← Voltar para a página do evento
              </Link>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
